import { Fragment, useState, useEffect } from 'react';
import { AppButton, AppTextField, PasswordInputField } from 'components';
import ESTDropDown from 'components/molecules/ESTDropDown';
import ESDropDown from 'components/molecules/ESDropDown';
import ESClassDropDown from 'components/molecules/ESClassDropDown';
import StreamsDropDown from 'components/molecules/StreamsDropDownType';
import CoursesMultiSelect from 'components/molecules/CoursesMultiSelect';
import useManageUser from 'hooks/API/useManageUser';
import useToaster from 'hooks/useToaster';
import {
  AppModal,
  AppModalBody,
  AppModalFooter,
  AppModalHeader,
} from 'components/molecules/Modal/AppModal';
import * as AppPics from 'assets/images';
import Joi from 'joi';

const ManageRoles2 = ({ handleSave, userDetails = {}, roleKey, roleId }) => {
  const [name, handleName] = useState('');
  const [email, handleEmail] = useState('');
  const [password, handlePassword] = useState('');
  const [mobile, handleMobile] = useState('');
  const [EST, handleEST] = useState('');
  const [ES, handleES] = useState('');
  const [ESClass, handleESClass] = useState('');
  const [formErrors, setFormErrors] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Modal fields
  const [modalCurriculum, setModalCurriculum] = useState({ id: '', name: '' });
  const [modalBoard, setModalBoard] = useState({ id: '', name: '' });
  const [modalStream, setModalStream] = useState({ id: '', name: '' });
  const [modalClass, setModalClass] = useState({ id: '', name: '' });
  const [modalCourses, setModalCourses] = useState([]);

  // Preview Data
  const [previewData, setPreviewData] = useState([]);

  const { isLoading: saveLoading, mutate, isError: saveError, isSuccess } = useManageUser();
  useToaster(saveLoading, { type: saveError ? 'error' : 'success' });

  // Populate previewData from userDetails on edit
  useEffect(() => {
    handleName(userDetails?.name || '');
    handleEmail(userDetails?.email || '');
    handleMobile(userDetails?.mobile || '');
    handleEST(userDetails?.est?._id || '');
    handleES(userDetails?.es?._id || '');
    handleESClass(userDetails?.class?._id || '');

    if (userDetails?.assignedCourses?.length) {
      const preview = userDetails.assignedCourses.map((c, i) => ({
        id: i,
        curriculum: c?.est?.name,
        curriculumId: c?.est?._id,
        board: c?.es?.name,
        boardId: c?.es?._id,
        stream: c.streamName,
        streamId: c.streamId,
        class: c?.classId?.name,
        classId: c?.classId?._id,
        assignedCourses: [
          {
            label: c?.courseName,
            value: c?._id,
          },
        ],
      }));
      setPreviewData(preview);
    }
  }, [userDetails]);

  useEffect(() => {
    if (isSuccess) {
      clearData();
      clearModalData();
    }
  }, [isSuccess]);

  function clearData() {
    handleName('');
    handleEmail('');
    handlePassword('');
    handleMobile('');
    handleEST('');
    handleES('');
    handleESClass('');
    setFormErrors([]);
    setPreviewData([]);
  }

  function clearModalData() {
    setModalCurriculum({ id: '', name: '' });
    setModalBoard({ id: '', name: '' });
    setModalStream({ id: '', name: '' });
    setModalClass({ id: '', name: '' });
    setModalCourses([]);
  }

  // Save function
  function save() {
    const schema = userDetails?._id ? manageStudentUserSchemaUpdate : manageStudentUserSchemaCreate;

    // Build data FIRST
    const data = {
      name,
      email,
      mobile,
      password,
      role: roleId,
      est: EST,
      es: ES,
      class: ESClass,
      assignedCourses: previewData.flatMap((entry) => entry.assignedCourses.map((c) => c.value)),
      assignedClasses: previewData.flatMap((entry) => (entry.classId ? [entry.classId] : [])),
      assignedSubjects: [],
      status: 'verified',
    };

    // Password handling
    if (!userDetails?._id) {
      // create
      data.password = password;
    } else {
      // update
      if (password.trim() !== '') {
        data.password = password;
      } else {
        delete data.password;
      }
    }

    // Validate AFTER data exists
    // const validation = schema.validate(data, {
    //   abortEarly: false,
    //   allowUnknown: true,
    // });

    const validation = schema.validate({ name, email, mobile, password }, { abortEarly: false });

    if (validation.error) {
      const errors = validation.error.details.map((d) => d.message);
      setFormErrors(errors);
      return;
    }

    if (saveLoading) return;

    mutate({
      type: userDetails?._id ? 'UPDATE' : 'CREATE',
      userId: userDetails?._id,
      ...data,
    });
  }

  // Modal OK
  const handleModalOk = () => {
    if (!modalCurriculum.id || !modalBoard.id || !modalClass.id || modalCourses.length === 0)
      return;

    const newEntry = {
      id: Date.now(),
      curriculum: modalCurriculum.name,
      curriculumId: modalCurriculum.id,
      board: modalBoard.name,
      boardId: modalBoard.id,
      stream: modalStream.name,
      streamId: modalStream.id,
      class: modalClass.name,
      classId: modalClass.id,
      assignedCourses: modalCourses.map((c) => ({ label: c.label, value: c.value })),
    };

    // Check if entry for same class already exists → replace it
    setPreviewData((prev) => {
      const filtered = prev.filter((e) => e.classId !== newEntry.classId);
      return [...filtered, newEntry];
    });

    clearModalData();
    setIsModalOpen(false);

    // Update main form dropdowns
    handleEST(modalCurriculum.id);
    handleES(modalBoard.id);
    handleESClass(modalClass.id);
  };

  // Edit preview
  const handleEditPreview = (entry) => {
    setModalCurriculum({ id: entry.curriculumId, name: entry.curriculum });
    setModalBoard({ id: entry.boardId, name: entry.board });
    setModalStream({ id: entry.streamId, name: entry.stream });
    setModalClass({ id: entry.classId, name: entry.class });
    setModalCourses(entry.assignedCourses);
    setIsModalOpen(true);
  };

  // Delete preview entry
  const handleDeletePreview = (entry) => {
    setPreviewData((prev) => prev.filter((e) => e.id !== entry.id));
  };

  const handleCancel = () => {
    clearData();
    clearModalData();
  };

  return (
    <>
      {/* Basic Fields */}
      <div className="col-lg-4 col-md-6 col-sm-12">
        <AppTextField
          labelProps={{ label: 'Name *', htmlFor: 'name' }}
          inputProps={{
            placeholder: 'Name *',
            id: 'name',
            value: name,
            onChange: (e) => handleName(e.target.value),
          }}
        />
      </div>
      <div className="col-lg-4 col-md-6 col-sm-12">
        <AppTextField
          labelProps={{ label: 'Mobile *', htmlFor: 'mobile' }}
          inputProps={{
            placeholder: 'Mobile *',
            id: 'mobile',
            value: mobile,
            onChange: (e) => handleMobile(e.target.value),
          }}
        />
      </div>
      <div className="col-lg-4 col-md-6 col-sm-12">
        <AppTextField
          labelProps={{ label: 'Email *', htmlFor: 'email' }}
          inputProps={{
            placeholder: 'Email *',
            id: 'email',
            value: email,
            onChange: (e) => handleEmail(e.target.value),
          }}
        />
      </div>
      <div className="col-lg-4 col-md-6 col-sm-12">
        <PasswordInputField
          labelProps={{ label: 'Password *', htmlFor: 'password' }}
          inputProps={{
            placeholder: 'Password *',
            id: 'password',
            value: password,
            onChange: (e) => handlePassword(e.target.value),
          }}
        />
      </div>

      {/* Assign Courses Button */}
      <div className="col-lg-4 col-md-6 col-sm-12 mt-5">
        <AppButton variantClass="btn-primary" onClick={() => setIsModalOpen(true)}>
          Assign Courses
        </AppButton>
      </div>

      {/* Preview Table */}
      {previewData.length > 0 && (
        <div className="col-12 mt-4">
          <h2 className="prime-head p-0">Course Assignments Preview</h2>
          <div className="tableWrap">
            <div className="table-responsive">
              <table className="table tableWrap__table">
                <thead className="thead-light">
                  <tr>
                    <th>#</th>
                    <th>Curriculum</th>
                    <th>Board</th>
                    <th>Stream</th>
                    <th>Class</th>
                    <th>Courses</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((entry, index) => (
                    <tr key={entry.id}>
                      <td>{index + 1}</td>
                      <td>{entry.curriculum}</td>
                      <td>{entry.board}</td>
                      <td>{entry.stream}</td>
                      <td>{entry.class}</td>
                      <td>{entry.assignedCourses.map((c) => c.label).join(', ')}</td>
                      <td>
                        <AppButton
                          onClick={() => handleEditPreview(entry)}
                          className="actions-icons mx-2"
                        >
                          <img src={AppPics.EditSvg} alt="edit" />
                        </AppButton>
                        <AppButton
                          onClick={() => handleDeletePreview(entry)}
                          className="actions-icons mx-2 text-danger"
                        >
                          <img src={AppPics.DeleteSvg} alt="delete" />
                        </AppButton>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Save & Cancel */}
      <div className="mt-4 d-flex flex-row">
        <AppButton
          variantClass="btn-primary mx-3"
          style={{ minWidth: '1.5rem' }}
          onClick={save}
          disabled={saveLoading || previewData.length === 0}
        >
          {saveLoading ? 'Saving...' : 'Save'}
        </AppButton>
        <AppButton variantClass="btn-plane p--tint mx-3" onClick={handleCancel}>
          Cancel
        </AppButton>
      </div>

      {/* Modal */}
      <AppModal open={isModalOpen} closeModal={() => setIsModalOpen(false)} size="lg">
        <AppModalHeader>
          <h5 className="modal-sub-text">Assign Courses</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setIsModalOpen(false)}
          ></button>
        </AppModalHeader>

        <AppModalBody>
          <div className="mb-3">
            <ESTDropDown
              enableAPI
              value={modalCurriculum.id}
              handleChange={(e, args) => {
                setModalCurriculum({
                  id: e.target.value,
                  name: e.target.options[e.target.selectedIndex].text,
                });
              }}
              label="Curriculum"
            />
          </div>
          <div className="mb-3">
            <ESDropDown
              enableAPI={!!modalCurriculum.id}
              dependencies={{ query: modalCurriculum.id ? `est=${modalCurriculum.id}` : '' }}
              value={modalBoard.id}
              handleChange={(e, args) =>
                setModalBoard({ id: e.target.value, name: args?.label || args?.text || '' })
              }
              label="Board"
              defaultLook={false}
            />
          </div>
          <div className="mb-3">
            <StreamsDropDown
              enableAPI
              value={modalStream.id}
              handleChange={(e, args) =>
                setModalStream({ id: e.target.value, name: args?.label || args?.text || '' })
              }
              label="Stream"
              defaultLook={false}
            />
          </div>
          <div className="mb-3">
            <ESClassDropDown
              enableAPI={!!modalBoard.id}
              dependencies={{ query: `esId=${modalBoard.id}` }}
              value={modalClass.id}
              handleChange={(e, args) =>
                setModalClass({ id: e.target.value, name: args?.label || args?.text || '' })
              }
              label="Class"
              defaultLook={false}
            />
          </div>
          <div className="mb-3">
            <CoursesMultiSelect
              enableAPI={!!modalClass.id}
              values={modalCourses}
              handleChange={(selected) => setModalCourses(selected)}
              dependencies={{
                query: { estId: modalCurriculum.id, esId: modalBoard.id, classId: modalClass.id },
              }}
            />
          </div>
        </AppModalBody>

        <AppModalFooter>
          <AppButton variantClass="btn-primary" onClick={handleModalOk}>
            OK
          </AppButton>
        </AppModalFooter>
      </AppModal>
    </>
  );
};

const manageStudentUserSchemaCreate = Joi.object({
  email: Joi.string().required(),
  name: Joi.string().required(),
  mobile: Joi.string().required(),
  password: Joi.string().min(8).required(),
});

const manageStudentUserSchemaUpdate = Joi.object({
  email: Joi.string().required(),
  name: Joi.string().required(),
  mobile: Joi.string().required(),
  password: Joi.string().allow('').optional(), // <- NOT required
});

export default ManageRoles2;
